/**
 * Everything awaiting a decision, across the organization.
 *
 * The administrator's view of what is stuck. It answers the question asked
 * when a policy has been sitting unpublished for two weeks and nobody knows
 * whose desk it is on.
 *
 * Deliberately not filtered to what the viewer could decide: an administrator
 * needs to see an instance sitting on a step whose role NOBODY currently
 * holds, because that is exactly what strands a document forever, and it is
 * invisible from every other page.
 */

import { dueText } from './stepText';
import late from './Approvals.module.css';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { call, isCallFailure } from '../../lib/callable';
import { ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import type { ApprovalInstance, ApprovalStep } from './useApprovals';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from '../admin/Admin.module.css';

interface OrgPending {
  instances: ApprovalInstance[];
  /** Parallel to `instances`; null where no step matched. */
  steps: (ApprovalStep | null)[];
  total: number;
}

/** How long an approval has been sitting, in whole days. */
function waitingDays(startedAt: unknown, now = new Date()): number | null {
  let date: Date | null = null;
  if (typeof startedAt === 'string') date = new Date(startedAt);
  else if (typeof startedAt === 'object' && startedAt !== null) {
    const v = startedAt as { toDate?: () => Date; _seconds?: number; seconds?: number };
    if (typeof v.toDate === 'function') date = v.toDate();
    else if (typeof v._seconds === 'number') date = new Date(v._seconds * 1000);
    else if (typeof v.seconds === 'number') date = new Date(v.seconds * 1000);
  }
  if (!date || Number.isNaN(date.getTime())) return null;
  return Math.floor((now.getTime() - date.getTime()) / 86_400_000);
}

export function ApprovalInboxPage() {
  const { session } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['org-pending-approvals', session?.orgId],
    enabled: Boolean(session?.orgId),
    retry: false,
    queryFn: () =>
      call<Record<string, never>, OrgPending>('listOrgPendingApprovalsFn', {}),
  });

  if (isLoading) return <Spinner label="Loading approvals" />;

  if (error) {
    return (
      <PageState title="Could not load the approval inbox" tone="alert">
        {isCallFailure(error) ? error.message : 'Something went wrong.'}
      </PageState>
    );
  }

  const instances = data?.instances ?? [];
  const steps = data?.steps ?? [];
  const stranded = steps.filter((s) => !s || !s.roleId).length;

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Approval inbox</h1>
        <p className={styles.subtitle}>
          Every document waiting on a decision anywhere in the organization,
          longest wait first.
        </p>
      </header>

      <div className={styles.body}>
        {instances.length === 0 ? (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            Nothing is waiting for approval.
          </div>
        ) : (
          <>
            {stranded > 0 && (
              <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
                <strong>
                  {stranded} {stranded === 1 ? 'document is' : 'documents are'} stuck
                  on a step nobody can decide.
                </strong>{' '}
                The step has no role assigned, or the workflow it belongs to has
                changed. Fix it in{' '}
                <Link to="/admin/approval-workflows">Approval workflows</Link>.
              </div>
            )}

            <dl className={own.tiles}>
              <div className={own.tile}>
                <dt className={own.tileKey}>Waiting</dt>
                <dd className={`${own.tileValue} tnum`}>{instances.length}</dd>
              </div>
              <div className={own.tile}>
                <dt className={own.tileKey}>Stuck</dt>
                <dd className={`${own.tileValue} tnum ${stranded ? own.danger : ''}`}>
                  {stranded}
                </dd>
              </div>
            </dl>

            <div className={own.scroller}>
              <table className={own.table}>
                <caption>Documents awaiting a decision, longest wait first</caption>
                <thead>
                  <tr>
                    <th scope="col">Document</th>
                    <th scope="col">Step</th>
                    <th scope="col">Decided by</th>
                    <th scope="col" className={own.numeric}>Waiting</th>
                  </tr>
                </thead>
                <tbody>
                  {instances
                    .map((inst, i) => ({ inst, step: steps[i] ?? null, days: waitingDays(inst.startedAt) }))
                    .sort((a, b) => (b.days ?? -1) - (a.days ?? -1))
                    .map(({ inst, step, days }) => (
                      <tr key={inst.instanceId}>
                        <td>
                          <Link to={`/policy/${inst.artifactId}`} className={own.link}>
                            Open document
                          </Link>
                        </td>
                        <td>
                          {step?.name ?? (
                            <span className={styles.absent}>Step {inst.currentStepOrder}</span>
                          )}
                          {dueText(step?.dueAt) && <div className={dueText(step?.dueAt)!.late ? late.late : late.itemMeta}>{dueText(step?.dueAt)!.text}</div>}
                        </td>
                        <td>
                          {step?.roleId && isRole(step.roleId) ? (
                            ROLE_DISPLAY_NAMES[step.roleId]
                          ) : (
                            <span className={`${own.pill} ${styles.danger}`}>Nobody</span>
                          )}
                        </td>
                        <td className={`${own.numeric} tnum`}>
                          {days === null ? (
                            <span className={styles.absent}>&mdash;</span>
                          ) : (
                            `${days} ${days === 1 ? 'day' : 'days'}`
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}
